import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import * as d3 from 'd3';
import { EidosGraphicChooserMethods, IEidosGraphicChooserConfig, IEidosGraphicChooserSelection, IEidosGraphicChooserPlaceInfoTooltip, IEidosGraphicChooserConnectedSelection, IEidosGraphicChooserRemappedSelection, IEidosGraphicChooserFeatures } from './eidos-graphic-chooser.model';
import { from, of } from 'rxjs';
import { catchError, concatMap, finalize, map, tap } from 'rxjs/operators';
import startCase from 'lodash/startCase';
import { v4 as uuid } from 'uuid';
import { EidosSvgNameSpace } from '@app/core/models/core-constant.model';

@Component({
    selector: 'eidos-graphic-chooser',
    encapsulation: ViewEncapsulation.None,
    templateUrl: './eidos-graphic-chooser.component.html',
    styleUrls: ['./eidos-graphic-chooser.component.scss'],
    standalone: false
})
export class EidosGraphicChooserComponent implements OnInit {

  /**
   * Configuration of component
   *  prefix to select the place
   *  array of selectable place (free and preferred type)
   *  array of unselectable place (busy)
   *
   * @type {IEidosGraphicChooserConfig}
   * @memberof EidosGraphicChooserComponent
   */
  @Input()
  public config: IEidosGraphicChooserConfig;

  /**
   * Crystal Grace SVGs use a viewBox about 3.4x smaller than Serenity/Symphony.
   */
  protected get isCrystalGraceDeckplan(): boolean {
    return (this.config?.folder ?? '').toLowerCase().includes('crystalgrace');
  }

  protected get cabinStrokeWidths(): {
    selectable: string;
    selected: string;
    connected: string;
    unselectable: string;
    reserved: string;
    actualReserved: string;
  } | null {
    if (!this.isCrystalGraceDeckplan) return null;
    return {
      selectable: '0.9px',
      selected: '0.9px',
      connected: '0.6px',
      unselectable: '0.3px',
      reserved: '0.3px',
      actualReserved: '0.3px',
    };
  }

  protected get cabinStatusFills(): {
    unselectable: string;
    reserved: string;
    actualReserved: string;
  } | null {
    if (!this.isCrystalGraceDeckplan) return null;
    return {
      unselectable: 'url(#pattern-diagonal-hatch-cg)',
      reserved: 'url(#pattern-diagonal2-hatch-cg)',
      actualReserved: 'url(#pattern-diagonal2-hatch-cg)',
    };
  }

  private resolveStatusFill(fill: string): string {
    if (!this.isCrystalGraceDeckplan || !fill.startsWith('url(#')) return fill;
    return fill.replace(/url\(#(pattern-[^)]+)\)/, (_m, id: string) =>
      id.endsWith('-cg') ? `url(#${id})` : `url(#${id}-cg)`
    );
  }

  /**
   * type that is valid for selection
   *
   * @type {Array<number>}
   * @memberof EidosGraphicChooserComponent
   */
  @Input()
  public filterLevels: Array<number>;

  /**
   * type that is valid for selection
   *
   * @type {Array<string>}
   * @memberof EidosGraphicChooserComponent
   */
  @Input()
  public selectables: Array<string | IEidosGraphicChooserSelection>;

  /**
   * arry of connected suites
   *
   * @type {Array<string>}
   * @memberof EidosGraphicChooserComponent
   */
  @Input()
  public connected: IEidosGraphicChooserConnectedSelection[];

  @Input()
  public features: IEidosGraphicChooserFeatures[];

  /**
   * arry of connected suites
   *
   * @type {Array<string>}
   * @memberof EidosGraphicChooserComponent
   */
  @Input()
  public remapped: IEidosGraphicChooserRemappedSelection[];


  /**
   * type that is not valid for selection because not availables (e.s. booked)
   *
   * @type {Array<string|IEidosGraphicChooserSelection>}
   * @memberof EidosGraphicChooserComponent
   */
  @Input()
  public unselectables: Array<string | IEidosGraphicChooserSelection>;

  /**
   * type that is not valid for selection because reserved
   *
   * @type {Array<string|IEidosGraphicChooserSelection>}
   * @memberof EidosGraphicChooserComponent
   */
  @Input()
  public reserved: Array<string | IEidosGraphicChooserSelection>;
  /**
   * type that is not valid for selection because anctul under selection for other choose
   *
   * @type {Array<string|IEidosGraphicChooserSelection>}
   * @memberof EidosGraphicChooserComponent
   */
  @Input()
  public actualReserved: Array<string | IEidosGraphicChooserSelection>;


  /**
   * Current selected place if is defined
   * for this property is active the two way binding
   *
   * @type {IEidosGraphicChooserSelection}
   * @memberof EidosGraphicChooserComponent
   */
  @Input()
  public selected?: IEidosGraphicChooserSelection;
  @Output()
  public selectedChange: EventEmitter<IEidosGraphicChooserSelection> = new EventEmitter<IEidosGraphicChooserSelection>();

  protected connectedSelection?: string | IEidosGraphicChooserSelection;

  /**
   * Array of info that are added like a tooltip on the place svg tag
   *
   * @type {IEidosGraphicChooserSelection}
   * @memberof EidosGraphicChooserComponent
   */
   @Input()
   public placeInfoTooltip: Array<IEidosGraphicChooserPlaceInfoTooltip> = [];

  /**
   * methods to intercat programmatically with the component
   *
   * @type {EidosGraphicChooserMethods}
   * @memberof EidosGraphicChooserComponent
   */
  @Input()
  public apiMethods?: EidosGraphicChooserMethods;
  @Output()
  public apiMethodsChange: EventEmitter<EidosGraphicChooserMethods> = new EventEmitter<EidosGraphicChooserMethods>();

  /**
   * event used to upadte the tooltip content projection
   *
   * @type {EventEmitter<IEidosGraphicChooserSelection>}
   * @memberof EidosGraphicChooserComponent
   */
  @Output()
  public showTooltip: EventEmitter<IEidosGraphicChooserSelection> = new EventEmitter<IEidosGraphicChooserSelection>();

  @Output()
  public changeSelection = new EventEmitter<IEidosGraphicChooserSelection>();

  protected onConfigure = false;
  public id: string;
  public svgXml: Array<XMLDocument>;
  private $container: HTMLElement | null;
  private idxLegend=-1;
  private legendSvg?: XMLDocument;
  private viewInitialized = false;
  private loadGeneration = 0;

  private defaultPlacePrefix = 'c';
  private defaultBackgroundPrefix = 'b';
  private defaultTextPrefix = 't';
  private defaultLevelPrefix = 'd';
  private defaultOverLayerSelectors = ['[id^="link"]', '[id^="ada"]', '#testi', '[id$="testi"]',];

  private tooltipDebounceTimer: any;
  private placeStatus = {
    selected: 'egc-selected',
    selectable: 'egc-selectable',
    connected: 'egc-connected',
    unselectable: 'egc-unselectable',
    reserved: 'egc-reserved',
    actualReserved: 'egc-actual-reserved'
  }
  private nullSelection;
  private parentNode: ParentNode | null = null;

  constructor() {
    this.id = uuid();
    this.config = {
      isValid: false,
      code: '',
      description: '',
      folder: '',
      svgTrasform: [],
      filePrefix: '',
      legend: '',
      levels: [],
      levelsTitle: [],
      version: 1
    } as IEidosGraphicChooserConfig;
    this.$container = null;
    this.filterLevels = [];
    this.selectables = [];
    this.connected = []
    this.features = []
    this.remapped = []
    this.unselectables = [];
    this.reserved = [];
    this.actualReserved = []
    this.svgXml = [];
    this.nullSelection = { selection: '' } as IEidosGraphicChooserSelection;
  }

  clearSelection() {
    //clear current selection
    const className = this.placeStatus.selected;
    document.querySelectorAll(`.${className}`).forEach(e => {
      e.classList.remove(className);
    });
  }
  resetConfig() {
    this.clearView();
    this.initializeView();
  }
  getPlaceTitle(idx:number):string {
    const placeNum = this.config.levels[idx] ?? 0;
    if(this.config.levelsTitle[placeNum]) return this.config.levelsTitle[placeNum] ?? '';
    const deckStr = startCase(this.config.filePrefix);
    if(idx >= 0 && idx < this.config.levels.length) return `${deckStr} ${this.config.levels[idx]}`;
    return deckStr;
  }
  show() {
    this.$container = window.document.getElementById(`${this.id}`);
    if (this.$container) {
      this.svgXml.forEach((svg, idx) => {
        if (svg.documentElement) {
          if (this.idxLegend === idx) {
            svg.documentElement.id = `${this.id}-legend}`;
            svg.documentElement.classList.add('legend');
            this.$container!.appendChild(svg.documentElement);
          }
          else {
            svg.documentElement.id = `${this.id}-${this.config.filePrefix}${this.config.levels[idx]}`;
            const node = document.createElement("div");
            node.id = `${this.id}-title-${this.config.filePrefix}${this.config.levels[idx]}`;
            node.classList.add('egc-level-title');
            const textnode = document.createTextNode(this.getPlaceTitle(idx));
            node.appendChild(textnode);
            this.$container!.appendChild(node);
            this.$container!.appendChild(svg.documentElement);
          }
        }
      });

      //prepare usefull selector
      //setup class for overlay elements
      this.prepareOverlayElement();

      // add tooltips events
      this.addPlaceEvents();

      //add tooltip place info
      this.updatePlaceInfoTooltip();

      //apply remapping rules
      this.remmapingElements()

      //Setup init status
      Object.values(this.placeStatus).forEach(s => this.initializeSelectionStatus(s));
      this.setLevelVisibility();
      this.viewInitialized = true;
    }

  }
  selectPlace(place: IEidosGraphicChooserSelection) {
    //set current selection
    this.selected = place;
    this.connectedSelection = this.connected.find(c=>this.checkSelection(c.selection,place))?.connected
    this.updateSelectionStatus(this.placeStatus.selected);
  }
  checkSelection(first:string|IEidosGraphicChooserSelection,second:string|IEidosGraphicChooserSelection) {
    if(first===second) return true
    if(this.config.version===1) {
      let selectionParts1 = typeof first === 'string' ? (first.includes('-') ? first.split('-') :[,,first]) : [,,first.selection];
      if (selectionParts1.length < 3) return false;
      let selectionParts2 = typeof second === 'string' ? (second.includes('-') ? second.split('-') :[,,second])  : [,,second.selection];
      if (selectionParts2.length < 3) return false;

      const prefix = this.config.placePrefix ?? this.defaultPlacePrefix
      return selectionParts1[2]?.replace(prefix,'') === selectionParts2[2]?.replace(prefix,'')
    }

    let selectionParts1 = typeof first === 'string' ? (first.includes('-') ? first.split('-') :[,first]) : [,first.selection];
    if (selectionParts1.length < 2) return false;
    let selectionParts2 = typeof second === 'string' ? (second.includes('-') ? second.split('-') :[,second])  : [,second.selection];
    if (selectionParts2.length < 2) return false;

    const prefix = this.config.placePrefix ?? this.defaultPlacePrefix
    return selectionParts1[1]?.replace(prefix,'') === selectionParts2[1]?.replace(prefix,'')
  }

  ngOnInit(): void {
    const api = new EidosGraphicChooserMethods();
    this.onConfigure = true;
    api.clearSelection = this.clearSelection.bind(this);
    api.selectPlace = this.selectPlace.bind(this);
    api.resetConfig = this.resetConfig.bind(this);
    api.show = this.show.bind(this);

    this.apiMethodsChange.emit(api);
  }

  emitSelectionChange(selection?: string) {
    if (!selection) return;
    const selectionParts = selection.split('-');
    if(this.config.version===2) {
      if (selectionParts.length < 2) return;
      const reLevel = new RegExp(`^${this.config.levelPrefix ?? this.defaultLevelPrefix}`, 'i');
      const rePlace = new RegExp(`^${this.config.placePrefix ?? this.defaultPlacePrefix}`, 'i');
      const sel = {
        level: +(selectionParts[0].replace(reLevel, '')),
        selection: selectionParts[1].replace(rePlace, ''),
      } as IEidosGraphicChooserSelection;
      this.selectedChange.emit(sel);
    } else {
      if (selectionParts.length < 3) return;
      const reLevel = new RegExp(`^${this.config.levelPrefix ?? this.defaultLevelPrefix}`, 'i');
      const rePlace = new RegExp(`^${this.config.placePrefix ?? this.defaultPlacePrefix}`, 'i');
      const sel = {
        level: +(selectionParts[0].replace(reLevel, '')),
        type: selectionParts[1],
        selection: selectionParts[2].replace(rePlace, ''),
      } as IEidosGraphicChooserSelection;
      this.selectedChange.emit(sel);
    }

  }

  onClickSelectable(selection?: string) {
    if(selection) this.connectedSelection = this.connected.find(c=>this.checkSelection(c.selection,selection))?.connected
    this.emitSelectionChange(selection);
    this.onMouseLeave();
  }

  onClickSelected(selection?: string) {
    this.emitSelectionChange(selection);
    this.onMouseLeave();
  }

  onClickUnselectable(_?: string) {
    this.onMouseLeave();
  }
  calculateRef(ref: string): IEidosGraphicChooserSelection {
    const selectionParts = ref.split('-');
    if (selectionParts.length < 3) return this.nullSelection;

    const reLevel = new RegExp(`^${this.config.levelPrefix ?? this.defaultLevelPrefix}`, 'i');
    const rePlace = new RegExp(`^${this.config.placePrefix ?? this.defaultPlacePrefix}`, 'i');
    return {
      level: +(selectionParts[0].replace(reLevel, '')),
      type: selectionParts[1],
      selection: selectionParts[2].replace(rePlace, ''),
    } as IEidosGraphicChooserSelection;
  }

  private tooltipPosition(tooltip:HTMLElement,evt:any) {
    const type = this.config.tooltip?.type ?? 'onside';

    switch(type) {
      case 'onpointer':
        if (evt.target) {
          const el = evt.target;
          if (el.id) {
            const node = document.createElementNS(EidosSvgNameSpace,"title");
            node.textContent = tooltip.innerText;
            el.childNodes.forEach((element:any) => {
              if(element.nodeName==='title') el.removeChild(element);
            });
            el.appendChild(node);
            this.showTooltip.emit(this.calculateRef(el.id));
          }
        };
        break;
      default:
        let width = 10;
        let height = 10;
        if (evt.target) {
          const el = evt.target;
          if (el.id) this.showTooltip.emit(this.calculateRef(el.id));
        };
        //calculate best position for view html
        tooltip.style.display = "block";
        tooltip.style.position = "absolute";
        if (evt.pageX > window.innerWidth / 2) {
          tooltip.style.left = width + 'px';
          tooltip.style.right = 'auto';
        } else {
          tooltip.style.right = width + 'px';
          tooltip.style.left = 'auto';
        }
        tooltip.style.top = height + 'px';
        break;
    }
  }

  openTooltip(evt: any) {
    const tooltip = document.getElementById(`${this.id}-tooltip`);
    if (!tooltip) return;

    this.parentNode = tooltip.parentNode;
    if (this.parentNode) {
      //move to body
      this.parentNode.removeChild(tooltip);
      const body = document.getElementsByTagName('body');
      if (body.length > 0) body[0].appendChild(tooltip)
    }

    //callback to update
    this.tooltipPosition(tooltip,evt);
  }
  removeTooltip(evt:any) {
    const type = this.config.tooltip?.type ?? 'onside';

    switch(type) {
      case 'onpointer':
        if (evt?.target) {
          evt.target.removeChild(evt.target.firstChild);
        };
        break;
      default:
        break;
    }
  }
  closeTooltip(evt: any) {
    this.removeTooltip(evt);
    if (this.tooltipDebounceTimer) {
      clearTimeout(this.tooltipDebounceTimer);
      this.tooltipDebounceTimer = undefined;
    }
    var tooltip = document.getElementById(`${this.id}-tooltip`);
    if (!tooltip) return;
    tooltip.style.display = "none";
    if (tooltip.parentNode && this.parentNode) {
      //move to body
      tooltip.parentNode.removeChild(tooltip);
      this.parentNode.appendChild(tooltip)
    }
  }
  onMouseEnter(evt: any) {
    const debounce = this.config.tooltip?.debounce ?? 0;
    if (debounce === 0) {
      this.openTooltip(evt);
      return;
    }

    if (this.tooltipDebounceTimer) clearTimeout(this.tooltipDebounceTimer);
    const self = this;
    this.tooltipDebounceTimer = setTimeout(() => self.openTooltip(evt), debounce);
  }
  onMouseLeave(evt?: any) {
    this.closeTooltip(evt);
  }
  initializeView() {
    if (!Array.isArray(this.config.levels) || this.config.levels.length === 0) return;

    const generation = ++this.loadGeneration;
    this.onConfigure = true;

    const firstDeck = this.config.levels[0];
    const remainingDecks = this.config.levels.slice(1);
    const legendPath = `${this.config.folder}/${this.config.legend}.svg`;
    const firstDeckPath = `${this.config.folder}/${this.config.filePrefix}${firstDeck}.svg`;

    // First paint: legend + first deck, then load the rest progressively.
    from(Promise.all([
      d3.xml(firstDeckPath).catch((err) => {
        console.log('egc: svg deck missing', firstDeckPath, err);
        return null as unknown as XMLDocument;
      }),
      d3.xml(legendPath).catch((err) => {
        console.log('egc: svg legend missing', legendPath, err);
        return null as unknown as XMLDocument;
      }),
    ])).pipe(
      tap(([firstSvg, legendSvg]) => {
        if (generation !== this.loadGeneration) return;
        if (firstSvg) {
          this.processSvg(firstSvg);
          this.svgXml.push(firstSvg);
        }
        if (legendSvg) {
          this.processSvg(legendSvg);
          this.legendSvg = legendSvg;
          this.idxLegend = this.svgXml.length; // legend follows deck docs in show()
          this.svgXml.push(legendSvg);
        }
        this.show();
        this.onConfigure = false;
      }),
      concatMap(() => {
        if (generation !== this.loadGeneration || remainingDecks.length === 0) {
          return of(null);
        }
        return from(remainingDecks).pipe(
          concatMap((deck, offset) => {
            const deckIdx = offset + 1;
            const svgPath = `${this.config.folder}/${this.config.filePrefix}${deck}.svg`;
            return from(d3.xml(svgPath)).pipe(
              map((svg) => ({ svg, deckIdx })),
              catchError((err) => {
                console.log('egc: svg deck missing', svgPath, err);
                return of(null);
              })
            );
          }),
          tap((loaded) => {
            if (!loaded || generation !== this.loadGeneration) return;
            this.processSvg(loaded.svg);
            this.insertDeckSvg(loaded.svg, loaded.deckIdx);
            this.appendDeckToView(loaded.svg, loaded.deckIdx);
          })
        );
      }),
      finalize(() => {
        if (generation === this.loadGeneration) {
          this.onConfigure = false;
        }
      })
    ).subscribe({
      error: (err) => {
        console.log('egc: resources', err);
        if (generation === this.loadGeneration) {
          this.onConfigure = false;
        }
      }
    });
  }

  private processSvg(svg: XMLDocument) {
    if (this.config.version === 2) {
      this.config.levels.forEach(deck => {
        svg.querySelectorAll(this.getAllCabinSelector(deck))
          .forEach(el => {
            if (el.id.includes(`-${this.config.textPrefix ?? this.defaultTextPrefix}`)) return;
            el.setAttributeNS(null, 'fill', 'none');
          });
      });
    }
    this.config.svgTrasform?.forEach(t => {
      const selector: IEidosGraphicChooserSelection = { selection: t.key };
      if (!!t.backgroundColor && t.backgroundColor !== '') {
        svg.querySelectorAll(this.getBkgSelector(selector))
          .forEach(el => {
            el.setAttributeNS(null, 'fill', t.backgroundColor);
          });
      }
      if (!!t.foregroundColor && t.foregroundColor !== '') {
        svg.querySelectorAll(this.getTextBoxSelector(selector))
          .forEach(el => {
            el.setAttributeNS(null, 'stoke', t.foregroundColor);
          });
      }
      svg.querySelectorAll(this.getHighlightSelector(selector))
        .forEach(el => {
          if (!!t.backgroundColor && t.backgroundColor !== '') {
            el.setAttributeNS(null, 'fill', t.backgroundColor);
          }
          el.classList.add(`type-${t.type.toLowerCase()}`);
          t.cssClasses
            .split(' ')
            .filter(c => c !== '')
            .forEach(c => {
              el.classList.add(c);
            });
        });
    });
    this.updateFeatures(svg);
  }

  /** Keep deck docs contiguous before the legend entry in svgXml. */
  private insertDeckSvg(svg: XMLDocument, deckIdx: number) {
    if (this.legendSvg && this.idxLegend >= 0) {
      this.svgXml.splice(deckIdx, 0, svg);
      this.idxLegend = this.svgXml.length - 1;
    } else {
      this.svgXml.push(svg);
    }
  }

  private appendDeckToView(svg: XMLDocument, deckIdx: number) {
    if (!this.viewInitialized || !this.$container || !svg.documentElement) return;

    const deck = this.config.levels[deckIdx];
    const deckRoot = svg.documentElement;
    deckRoot.id = `${this.id}-${this.config.filePrefix}${deck}`;
    const title = document.createElement('div');
    title.id = `${this.id}-title-${this.config.filePrefix}${deck}`;
    title.classList.add('egc-level-title');
    title.appendChild(document.createTextNode(this.getPlaceTitle(deckIdx)));

    // Insert before legend when present so stacked order stays decks then legend.
    const legendEl = this.$container.querySelector(`[id="${this.id}-legend}"]`);
    if (legendEl) {
      this.$container.insertBefore(title, legendEl);
      this.$container.insertBefore(deckRoot, legendEl);
    } else {
      this.$container.appendChild(title);
      this.$container.appendChild(deckRoot);
    }

    (this.config.layerOverSelectors ?? this.defaultOverLayerSelectors).forEach(s => {
      deckRoot.querySelectorAll(s).forEach(oe => {
        if (oe.tagName === 'g') {
          Array.from(oe.children).forEach(n => n.classList.add('egc-overlay'));
        } else {
          oe.classList.add('egc-overlay');
        }
      });
    });

    if (this.config.tooltip?.enabled) {
      deckRoot.querySelectorAll(this.getTooltipselector()).forEach(e => {
        e.addEventListener('mouseenter', this.onMouseEnter.bind(this), true);
        e.addEventListener('mouseleave', this.onMouseLeave.bind(this));
      });
    }

    this.placeInfoTooltip.forEach(info => {
      deckRoot.querySelectorAll(this.getHighlightSelector(info.selection)).forEach(e => {
        const node = document.createElementNS(EidosSvgNameSpace, 'title');
        node.textContent = info.info;
        e.childNodes.forEach(element => {
          if (element.nodeName === 'title') e.removeChild(element);
        });
        e.appendChild(node);
      });
    });

    this.remmapingElements();
    Object.values(this.placeStatus).forEach(s => this.initializeSelectionStatus(s));
    this.setLevelVisibility();
  }
  updateFeatures(svg:XMLDocument) {

    this.features.forEach(f=>{
      svg.querySelectorAll(this.getHighlightSelector(f.selection)).forEach(e => {
        const txtEle = svg.querySelectorAll(this.getTextSelector(f.selection))[0]
        let x=0
        let y=0
        if(txtEle) {
          const transform = txtEle.getAttribute('transform')
          if(transform) {
            transform.split(')').forEach(t=>{
              if(t.startsWith('translate(')) {
                const xy = t.replace('translate(','').split(' ')
                x = +xy[0]
                y = +xy[1] -6
              }
            })
          }
        }
        if(x===0 || y===0) {
          x = +(e.getAttribute('x') ?? 0)
          if(x>0) x += 2
          y = +(e.getAttribute('y') ?? 0)
          if(y>0) y += 8
        }
        if(x===0 || y===0) return

        f.icons.forEach(i=>{
          var element = document.createElementNS('http://www.w3.org/2000/svg', 'text');
          element.setAttributeNS(null, 'x', `${x}`);
          element.setAttributeNS(null, 'y', `${y}`);
          var txt = document.createTextNode(i);
          element.setAttributeNS(null,'transfrom',`scale(0.5)`)
          if(f.classes) f.classes.forEach(c=>element.classList.add(c))
          if(f.cssStyle) element.setAttributeNS(null, 'style', f.cssStyle);
          element.appendChild(txt);
          e.parentElement?.appendChild(element);

        })
      })
    })
  }
  remmapingElements() {
    this.remapped.forEach(r=>{
      this.applyRemappaingOnElements(r)
    })
  }

  applyRemappaingOnElements(remapping:IEidosGraphicChooserRemappedSelection) {
    if(!this.$container) return
    this.$container.querySelectorAll(this.getHighlightSelector(remapping.selection)).forEach(e => {
      if (remapping.excludeTags && remapping.excludeTags.includes(e.tagName)) return;

      if(e.id) {
        const bId = `[id$=-b${remapping.selection.selection}]`

        //change id
        if(remapping.selectionChange) {
          const idparts = e.id.split('-')
          if(idparts.length>2) {
            const level = remapping.selection.level ? remapping.selection.level : idparts[0]
            const type = remapping.selection.type ? remapping.selection.type : idparts[1]
            const selection = remapping.selection.selection ? remapping.selection.selection : idparts[2]
            e.setAttribute('id',`${level}-${type}-c${selection}`.toLowerCase());
          }
        }

        //change styles
        if(remapping.applyStyles) {
          this.$container!.querySelectorAll(bId).forEach(be => {
            remapping.applyStyles!.forEach(s=>{
              this.updateStyleOptions(be,s.property,s.value)
            })
          })

        }

        //add class
        if(remapping.addClasses) {
          e.classList.add(...remapping.addClasses)
        }

      }
    });
  }
  clearView() {
    this.loadGeneration++;
    const $container = document.getElementById(`${this.id}`);
    if ($container) $container.innerHTML = '';
    this.svgXml = [];
    this.legendSvg = undefined;
    this.idxLegend = -1;
    this.viewInitialized = false;
    this.$container = null;
    this.removePlaceStatus();
  }
  prepareOverlayElement() {
    if (!this.$container) return;

    (this.config.layerOverSelectors ?? this.defaultOverLayerSelectors).forEach(s => {
      const overElements = this.$container!.querySelectorAll(s);
      overElements.forEach(oe => {
        if (oe.tagName === 'g') {
          Array.from(oe.children).forEach(n => n.classList.add('egc-overlay'));
        } else {
          oe.classList.add('egc-overlay');
        }
      });
    });
  }
  addPlaceEvents() {
    if (!this.$container) return;
    if (!this.config.tooltip?.enabled) return;
    this.$container.querySelectorAll(this.getTooltipselector())
      .forEach(e => {
        e.addEventListener('mouseenter', this.onMouseEnter.bind(this), true);
        e.addEventListener('mouseleave', this.onMouseLeave.bind(this));
      });
  }
  removePlaceStatus() {
    if (!this.$container) return;
    this.$container.querySelectorAll(this.getTooltipselector())
      .forEach(e => {
        e.removeEventListener('mouseenter', this.onMouseEnter.bind(this), true);
        e.removeEventListener('mouseleave', this.onMouseLeave.bind(this));
      });
  }
  getTooltipselector(): string {
    return `polygon[id^="${this.config.levelPrefix ?? this.defaultLevelPrefix}"][id*="-${this.config.placePrefix ?? this.defaultPlacePrefix}"], polyline[id^="${this.config.levelPrefix ?? this.defaultLevelPrefix}"][id*="-${this.config.placePrefix ?? this.defaultPlacePrefix}"], rect[id^="${this.config.levelPrefix ?? this.defaultLevelPrefix}"], path[id^="${this.config.levelPrefix ?? this.defaultLevelPrefix}"][id*="-${this.config.placePrefix ?? this.defaultPlacePrefix}"`;
  }
  composeSelector(prefix:string,s: string | IEidosGraphicChooserSelection){
    if(this.config.version===2) {
      return (typeof s === 'string' || s instanceof String)
      ?
      `.type-${s.toLowerCase()}`
      :
        (
          s.selection
          ?
          `[id$="-${prefix}${s.selection}"]`
          :
            (
              s.type
              ?
              `.type-${s.type.toLowerCase()}`
              :
              `[id^="${this.config.levelPrefix ?? this.defaultLevelPrefix}${s.level}-"]`
              )
        );
    }

    return (typeof s === 'string' || s instanceof String)
          ?
          `[id*="-${s.toLowerCase()}-${prefix}"]`
          :
            (
              s.selection
              ?
              `[id$="-${prefix}${s.selection}"]`
              :
                (
                  s.type
                  ?
                  `[id*="-${s.type.toLowerCase()}-${prefix}"]`
                  :
                  `[id^="${this.config.levelPrefix ?? this.defaultLevelPrefix}${s.level}-"]`
                )
            );
  }
  getTextBoxSelector(s: string | IEidosGraphicChooserSelection): string {
    return this.composeSelector(this.config.textPrefix ?? this.defaultTextPrefix,s)
  }
  getAllCabinSelector(deck: number): string {
    return `[id^="${this.config.levelPrefix ?? this.defaultLevelPrefix}${deck}-"]`
  }
  getBkgSelector(s: string | IEidosGraphicChooserSelection): string {
    return this.composeSelector(this.config.backgroundPrefix ?? this.defaultBackgroundPrefix,s)
  }
  getHighlightSelector(s: string | IEidosGraphicChooserSelection): string {
    return this.composeSelector(this.config.placePrefix ?? this.defaultPlacePrefix,s)
  }
  getTextSelector(s: string | IEidosGraphicChooserSelection): string {
    return (typeof s === 'string' || s instanceof String) ? `[id*="-${s.toLowerCase()}-t"]` : (s.selection ? `[id$="-t${s.selection}"]` : (s.type ? `[id*="-${s.type.toLowerCase()}-t"]`: `[id^="${this.config.levelPrefix ?? this.defaultLevelPrefix}${s.level}-"]`));
  }
  initializeSelectionStatus(className: string) {
    if (!this.$container) return;
    if (className === this.placeStatus.selectable) {

      //highlight all selectables
      this.selectables.forEach(s => {
        this.$container!.querySelectorAll(this.getHighlightSelector(s)).forEach(e => {
          e.addEventListener('click', this.onClickSelectable.bind(this, e.getAttribute('id') ?? undefined));
        });
      });
    } else if (className === this.placeStatus.unselectable) {

      //highlight all unselectables
      this.unselectables.forEach(s => {
        this.$container!.querySelectorAll(this.getHighlightSelector(s)).forEach((e: Element) => {
          if (e.removeAllListeners) e.removeAllListeners('click');
          e.addEventListener('click', this.onClickUnselectable.bind(this, e.getAttribute('id') ?? undefined));
        });
      });
    } else if (className === this.placeStatus.reserved) {

      //highlight all reserved
      this.reserved.forEach(s => {
        this.$container!.querySelectorAll(this.getHighlightSelector(s)).forEach((e: Element) => {
          if (e.removeAllListeners) e.removeAllListeners('click');
          e.addEventListener('click', this.onClickUnselectable.bind(this, e.getAttribute('id') ?? undefined));
        });
      });
    } else if (className === this.placeStatus.actualReserved) {

      //highlight all actual reserved
      this.actualReserved.forEach(s => {
        this.$container!.querySelectorAll(this.getHighlightSelector(s)).forEach((e: Element) => {
          if (e.removeAllListeners) e.removeAllListeners('click');
          e.addEventListener('click', this.onClickUnselectable.bind(this, e.getAttribute('id') ?? undefined));
        });
      });
    }

    this.updateSelectionStatus(className);
    this.setLevelVisibility();
  }
  dragPlace(ev:any, data:any) {
    ev.dataTransfer.setData("text", data);
  }

  dropPlace(ev:any, data:any) {
      ev.preventDefault();
      var data = ev.dataTransfer.getData("text");
      ev.target.appendChild(document.getElementById(data));

  }
  updateSelectionStatus(className: string) {
    if (!this.$container) return;

    //clear
    this.$container.querySelectorAll(`.${className}`).forEach(e => {
      e.classList.remove(className);
    });
    if (className === this.placeStatus.selectable) {
      this.$container.querySelectorAll(`.${this.placeStatus.connected}`).forEach(e => {
        e.classList.remove(this.placeStatus.connected);
      });
    }

    if (className === this.placeStatus.selectable) {

      //highlight all selectables
      this.selectables.forEach(s => {
        this.$container!.querySelectorAll(this.getHighlightSelector(s)).forEach(e => {
          if (e.tagName == 'text') return;
          e.classList.add(className);
          if(typeof s !== 'string') this.updateCustomOptions(e,s as IEidosGraphicChooserSelection);
        });
      });
    } else if (className === this.placeStatus.unselectable) {

      //highlight all unselectables
      this.unselectables.forEach(s => {
        this.$container!.querySelectorAll(this.getHighlightSelector(s)).forEach(e => {
          if (e.tagName == 'text') return;
          e.classList.remove(this.placeStatus.selectable);
          e.classList.add(className);
          if(typeof s !== 'string') this.updateCustomOptions(e,s as IEidosGraphicChooserSelection);
        });
      });

    } else if (className === this.placeStatus.reserved) {

      //highlight all reserved
      this.reserved.forEach(s => {
        this.$container!.querySelectorAll(this.getHighlightSelector(s)).forEach(e => {
          if (e.tagName == 'text') return;
          e.classList.remove(this.placeStatus.selectable);
          e.classList.add(className);
          if(typeof s !== 'string') this.updateCustomOptions(e,s as IEidosGraphicChooserSelection);
        });
      });

    } else if (className === this.placeStatus.actualReserved) {

      //highlight all actual reserved
      this.actualReserved.forEach(s => {
        this.$container!.querySelectorAll(this.getHighlightSelector(s)).forEach(e => {
          if (e.tagName == 'text') return;
          e.classList.remove(this.placeStatus.selectable);
          e.classList.add(className);
          if(typeof s !== 'string') this.updateCustomOptions(e,s as IEidosGraphicChooserSelection);
        });
      });

    } else if (className === this.placeStatus.selected) {

      this.changeSelection.emit(this.selected);

      //highlight current selection
      if (this.selected?.selection) {
        this.$container.querySelectorAll(this.getHighlightSelector(this.selected)).forEach(e => {
          if (e.tagName === 'polygon' || e.tagName === 'polyline' || e.tagName === 'rect' || e.tagName === 'path') e.classList.add(className);
          const pe = e.parentElement;
          //move last
          if (pe) {
            pe.removeChild(e);
            pe.appendChild(e);
            //move over element last
            (this.config.layerOverSelectors ?? this.defaultOverLayerSelectors).forEach(s => {
              const overElements = pe.querySelectorAll(s);
              overElements.forEach(oe => {
                pe.removeChild(oe);
                pe.appendChild(oe);
              });
            });
          }
        });
      }


      if (this.connectedSelection) {
        this.$container.querySelectorAll(this.getHighlightSelector(this.connectedSelection)).forEach(e => {
          if (e.tagName === 'polygon' || e.tagName === 'polyline' || e.tagName === 'rect'|| e.tagName === 'path') e.classList.add(className);
          const pe = e.parentElement;
          //move last
          if (pe) {
            pe.removeChild(e);
            pe.appendChild(e);
            //move over element last
            (this.config.layerOverSelectors ?? this.defaultOverLayerSelectors).forEach(s => {
              const overElements = pe.querySelectorAll(s);
              overElements.forEach(oe => {
                pe.removeChild(oe);
                pe.appendChild(oe);
              });
            });
          }
        });

      }

    }

    //higligth or fill connected like it master cabin
    this.selectables.forEach(s => {
      this.$container!.querySelectorAll(this.getHighlightSelector(s)).forEach(e => {
        if (e.tagName == 'text') return;

        const connectedSelection = this.connected.find(c=>this.checkSelection(c.selection,e.id))?.connected
        if(connectedSelection) {
          if(typeof connectedSelection !== 'string' && connectedSelection.selection === '') return
          this.$container!.querySelectorAll(this.getHighlightSelector(connectedSelection)).forEach(item => {
            item.classList.add(this.placeStatus.connected);
          });
        }

      });
    });
    this.reserved.forEach(s => {
      this.$container!.querySelectorAll(this.getHighlightSelector(s)).forEach(e => {
        if (e.tagName == 'text') return;

        const connectedSelection = this.connected.find(c=>this.checkSelection(c.selection,e.id))?.connected
        if(connectedSelection) {
          if(typeof connectedSelection !== 'string' && connectedSelection.selection === '') return
          this.$container!.querySelectorAll(this.getHighlightSelector(connectedSelection)).forEach(item => {
            item.classList.remove(this.placeStatus.selectable);
            item.classList.add(this.placeStatus.reserved);
          });
        }

      });
    });
    this.unselectables.forEach(s => {
      this.$container!.querySelectorAll(this.getHighlightSelector(s)).forEach(e => {
        if (e.tagName == 'text') return;

        const connectedSelection = this.connected.find(c=>this.checkSelection(c.selection,e.id))?.connected
        if(connectedSelection) {
          if(typeof connectedSelection !== 'string' && connectedSelection.selection === '') return
          this.$container!.querySelectorAll(this.getHighlightSelector(connectedSelection)).forEach(item => {
            item.classList.remove(this.placeStatus.selectable);
            item.classList.add(this.placeStatus.unselectable);
          });
        }

      });
    });

  }
  updateCustomOptions(e: Element, selection: IEidosGraphicChooserSelection) {
    if(selection.stroke) {
      let cs = e.getAttribute('style') ?? '';
      if(cs.length>0 && !cs.endsWith(';')) cs += ';'
      e.setAttribute('style',`${cs}stroke:${selection.stroke} !important`);
    }
    if(selection.fill) {
      let cs = e.getAttribute('style') ?? '';
      if(cs.length>0 && !cs.endsWith(';')) cs += ';'
      const fill = this.resolveStatusFill(selection.fill);
      e.setAttribute('style',`${cs}fill:${fill} !important; fill-opacity: 0.8;`);
    }
  }
  updateStyleOptions(e: Element, property: string, value: string) {
    let cs = e.getAttribute('style') ?? '';
    if(cs.length>0 && !cs.endsWith(';')) cs += ';'
    e.setAttribute('style',`${cs}${property}:${value} !important`);
  }
  setLevelVisibility() {
    if (!this.$container) return;
    if (!this.config.levels) return;

    this.$container.querySelectorAll(`[id^="${this.id}-${this.config.filePrefix}"]`).forEach(svg => svg.classList.remove('egc-hide'));
    this.$container.querySelectorAll(`[id^="${this.id}-title-${this.config.filePrefix}"]`).forEach(svg => svg.classList.remove('egc-hide'));

    if (this.filterLevels.filter(l => l > 0).length == 0) return; //no filter

    this.config.levels
      .filter(l => !this.filterLevels.includes(l))
      .forEach(l => {
        this.$container!.querySelector(`[id="${this.id}-${this.config.filePrefix}${l}"]`)?.classList.add('egc-hide');
        this.$container!.querySelector(`[id="${this.id}-title-${this.config.filePrefix}${l}"]`)?.classList.add('egc-hide');
      });

  }
  private updatePlaceInfoTooltip(): void {
    this.placeInfoTooltip.forEach(info=>{
      this.$container?.querySelectorAll(this.getHighlightSelector(info.selection)).forEach(e => {

        const node = document.createElementNS(EidosSvgNameSpace,"title");
        node.textContent = info.info;
        e.childNodes.forEach(element => {
          if(element.nodeName==='title') e.removeChild(element);
        });
        e.appendChild(node);
      });
    });
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes.config) {
      this.resetConfig();
      return;
    };

    this.onConfigure=true;
    if (changes.selectables) {
      Object.values(this.placeStatus).forEach(s => this.initializeSelectionStatus(s));
      this.updateSelectionStatus(this.placeStatus.selectable);
    };
    if (changes.unselectables) {
      Object.values(this.placeStatus).forEach(s => this.initializeSelectionStatus(s));
      this.updateSelectionStatus(this.placeStatus.unselectable);
    };
    if (changes.reserved) {
      this.updateSelectionStatus(this.placeStatus.reserved);
    };
    if (changes.actualReserved) {
      this.updateSelectionStatus(this.placeStatus.actualReserved);
    };
    if (changes.selected) {
      this.updateSelectionStatus(this.placeStatus.selected);
    };
    if (changes.filterLevels) {
      this.setLevelVisibility();
    }
    if (changes.placeInfoTooltip) {
      this.updatePlaceInfoTooltip();
    }
    this.onConfigure = false;

  }
}


